import {castDraft, type Draft, produce} from 'immer';

import {startFunctionPerformanceMeasure} from '../../../util/datadog/workspace/functionPerformance';
import type {AgentChatAction} from './actions';
import {applyRuntimeEventToThread} from './eventReducer';
import {addMessageNode, switchBranch} from './messageTree';
import {createRunDisplayNameKey} from './runDisplayNameKey';
import type {AgentChatState, ContentPart, Thread} from './thread';
import {
  createDefaultState,
  createEmptyThread,
  createThreadFromSummary,
  findThread,
  isActiveThread,
} from './thread';

export function agentChatReducer(
  state: AgentChatState = createDefaultState(),
  action: AgentChatAction
): AgentChatState {
  const endMeasure =
    action.type === 'loadThread'
      ? startFunctionPerformanceMeasure('immerLoadAgentThread')
      : undefined;
  const result = produce(state, immerDraft => {
    switch (action.type) {
      case 'createThread': {
        const thread = createEmptyThread(action.id, action.timestamp);
        // A new chat starts in `drafts` keyed by a client UUID before
        // getting promoted to  `threads` on first `turn-created` (in `applyRuntimEvent`)
        // NOTE: Use 'castDraft' to avoid "excessively deep" errors with
        // 'WritableDraft' inference (due to 'ReadonlyJSONValue').
        immerDraft.drafts[action.id] = castDraft(thread);
        immerDraft.activeThreadId = action.id;
        break;
      }

      case 'deleteThread': {
        // Capture before any deletions: the alias chain that resolves a
        // sticky draft-UUID `activeThreadId` to this thread's canonical id is
        // wiped below, so an alias-aware check after the deletions would
        // false-negative.
        const wasActive = isActiveThread(immerDraft, action.threadId);

        delete immerDraft.threads[action.threadId];
        delete immerDraft.drafts[action.threadId];
        delete immerDraft.promotionAliases[action.threadId];
        delete immerDraft.threadLoads[action.threadId];

        for (const [oldKey, newKey] of Object.entries(
          immerDraft.promotionAliases
        )) {
          if (newKey === action.threadId) {
            delete immerDraft.promotionAliases[oldKey];
          }
        }

        delete immerDraft.pendingRenames[action.threadId];
        immerDraft.pendingDeletes.delete(action.threadId);
        immerDraft.deletedThreadIds.add(action.threadId);
        if (wasActive) {
          immerDraft.activeThreadId = null;
        }
        break;
      }

      case 'setActiveThread': {
        immerDraft.activeThreadId = action.threadId;
        break;
      }

      case 'renameThread': {
        const thread = findThread(immerDraft, action.threadId);
        if (thread) {
          // Skip stale rename responses; either 'mergeThreadSummaries' or
          // 'loadThread' already refreshed the title.
          if (action.timestamp < thread.updatedAt) {
            break;
          }
          thread.title = action.title;
          thread.updatedAt = action.timestamp;
        }
        break;
      }

      case 'loadThread': {
        const id = action.thread.id;
        // A load that lands after the thread was deleted must not resurrect the
        // row; just drop its transient load state.
        if (state.deletedThreadIds.has(id) || state.pendingDeletes.has(id)) {
          delete immerDraft.threadLoads[id];
          break;
        }
        // The backend does not report cancel intent, so carry it forward while
        // the same turn is still running.
        const existing = immerDraft.threads[id];
        const carryCancelRequested =
          existing?.cancelRequested === true &&
          action.thread.status === 'running' &&
          action.thread.latestTurnId === existing.latestTurnId;
        // NOTE: The Thread annotation keeps Draft off the anonymous spread
        // type, which trips TS's instantiation depth limit.
        let nextThread: Thread = carryCancelRequested
          ? {...action.thread, cancelRequested: true}
          : action.thread;
        if (existing && existing.updatedAt > nextThread.updatedAt) {
          nextThread = {
            ...nextThread,
            title: existing.title,
            isPinned: existing.isPinned,
            updatedAt: existing.updatedAt,
          };
        }
        delete immerDraft.drafts[id];
        immerDraft.threads[id] = castDraft(nextThread);
        delete immerDraft.threadLoads[id];
        break;
      }

      case 'beginLoadThread': {
        // Only marks load state; callers activate the thread via
        // `setActiveThread`.
        immerDraft.threadLoads[action.threadId] = {status: 'loading'};
        break;
      }

      case 'failLoadThread': {
        // Drop a failure that lands after the thread already loaded; a stale
        // terminal event must not blank out content the user has, and a loaded
        // thread keeps no `threadLoads` entry.
        if (findThread(immerDraft, action.threadId)?.loadStatus === 'loaded') {
          break;
        }
        immerDraft.threadLoads[action.threadId] = {
          status: 'failed',
          failure: action.failure,
        };
        break;
      }

      case 'clearThreadLoad': {
        delete immerDraft.threadLoads[action.threadId];
        break;
      }

      case 'addUserMessage': {
        const thread = findThread(immerDraft, action.threadId);
        if (thread) {
          const isFirstMessage = thread.messageTree.rootChildren.length === 0;
          addMessageNode(thread.messageTree, action.parentId, action.message);
          thread.lastActivityAt = action.timestamp;
          if (isFirstMessage) {
            if (thread.projects.length === 0) {
              const project = deriveProjectFromContent(action.message.content);
              if (project) {
                thread.projects = [project];
              }
            }
          }
        }
        break;
      }

      case 'switchBranch': {
        const thread = findThread(immerDraft, action.threadId);
        if (thread) {
          switchBranch(thread.messageTree, action.targetNodeId);
        }
        break;
      }

      case 'applyRuntimeEvent': {
        const thread = findThread(immerDraft, action.threadId);
        if (!thread) {
          break;
        }

        // Drop events from runs that have already ended. Tool results may race
        // just after terminal SSE, so still allow them to settle in-flight rows.
        const outcome = thread.runOutcomes[action.runId];
        if (outcome) {
          if (
            action.event.type === 'turn-created' &&
            thread.latestTurnId == null
          ) {
            applyRuntimeEventToThread(thread, action.event, action.timestamp);
          } else if (
            outcome === 'finished' &&
            action.event.type === 'tool-call-result'
          ) {
            applyRuntimeEventToThread(thread, action.event, action.timestamp);
          }
          break;
        }

        applyRuntimeEventToThread(thread, action.event, action.timestamp);
        break;
      }

      case 'setThreadStatus': {
        const thread = findThread(immerDraft, action.threadId);
        if (thread) {
          thread.status = action.status;
        }
        break;
      }

      case 'setThreadError': {
        const thread = findThread(immerDraft, action.threadId);
        if (thread) {
          thread.error = action.error;
        }
        break;
      }

      case 'setCancelRequested': {
        const thread = findThread(immerDraft, action.threadId);
        if (thread) {
          thread.cancelRequested = action.value;
        }
        break;
      }

      case 'resolveRunDisplayNames': {
        const thread = findThread(immerDraft, action.threadId);
        if (!thread) {
          break;
        }

        for (const node of Object.values(thread.messageTree.nodes)) {
          for (const part of node.content) {
            if (part.type !== 'wandb-run') {
              continue;
            }

            const key = createRunDisplayNameKey(part);
            const result = action.results[key];
            if (!result) {
              continue;
            }

            if (result.status === 'resolved') {
              part.runDisplayName = result.displayName;
              part.runDisplayNameStatus = 'resolved';
            } else {
              delete part.runDisplayName;
              part.runDisplayNameStatus = result.status; // 'not-found' | 'error'
            }
          }
        }
        break;
      }

      case 'beginRename': {
        immerDraft.pendingRenames[action.threadId] = {title: action.title};
        break;
      }

      case 'endRename': {
        delete immerDraft.pendingRenames[action.threadId];
        break;
      }

      case 'beginDelete': {
        immerDraft.pendingDeletes.add(action.threadId);
        if (isActiveThread(immerDraft, action.threadId)) {
          immerDraft.activeThreadId = null;
        }
        break;
      }

      case 'endDelete': {
        immerDraft.pendingDeletes.delete(action.threadId);
        break;
      }

      case 'promoteDraft': {
        promoteDraftToThread(immerDraft, action.oldKey, action.threadId);
        break;
      }

      case 'mergeThreadSummaries': {
        for (const summary of action.summaries) {
          // Skip ids the user has just deleted (or is deleting); a list
          // refetch in flight when the delete fired could otherwise
          // re-add the row.
          if (
            state.pendingDeletes.has(summary.id) ||
            state.deletedThreadIds.has(summary.id)
          ) {
            continue;
          }

          // Promoted threads are keyed by server thread id.
          const existing = immerDraft.threads[summary.id];
          if (!existing) {
            immerDraft.threads[summary.id] = castDraft(
              createThreadFromSummary(summary)
            );
            continue;
          }

          // Skip stale metadata refetches. `updatedAt` is metadata-only, while
          // local streaming and messages bump `lastActivityAt`.
          if (summary.updatedAt < existing.updatedAt) {
            continue;
          }

          existing.title = summary.title;
          existing.isPinned = summary.isPinned;
          existing.updatedAt = summary.updatedAt;
          existing.lastActivityAt = Math.max(
            existing.lastActivityAt,
            summary.lastActivityAt
          );
          // Only refresh projects on thread summaries; loaded threads own the
          // full derivation.
          if (existing.loadStatus === 'summary') {
            existing.projects = summary.projects;
          }
        }
        break;
      }

      default: {
        const _exhaustive: never = action;
        void _exhaustive;
        break;
      }
    }
  });
  if (action.type === 'loadThread') {
    endMeasure?.({
      rootMessageCount: action.thread.messageTree.rootChildren.length,
    });
  }
  return result;
}

/**
 * promoteDraftToThread moves a client-created chat "draft" into the `threads`
 * bucket under the server-assigned `threadId`. Triggered by the first
 * `turn-created` event for a draft.
 *
 * If `threads[threadId]` is empty (i.e., user sends a new chat), the draft
 * becomes the new thread. If it's not (e.g., a `mergeThreadSummaries` or
 * `loadThread` happened concurrently), we do a selective merge.
 */
function promoteDraftToThread(
  immerDraft: Draft<AgentChatState>,
  oldKey: string,
  threadId: string
): void {
  const fromDraft = immerDraft.drafts[oldKey];
  if (!fromDraft) {
    return;
  }

  fromDraft.id = threadId;

  const existing = immerDraft.threads[threadId];

  // Merge: keep server metadata, incorporate live runtime state from the draft
  if (existing) {
    existing.id = threadId;
    existing.latestTurnId = fromDraft.latestTurnId;
    existing.messageTree = fromDraft.messageTree;
    existing.status = fromDraft.status;
    existing.currentRunId = fromDraft.currentRunId;
    existing.currentMessageId = fromDraft.currentMessageId;
    // Carry the cancel intent so a stop clicked on the draft before
    // turn-created survives the id rotation.
    existing.cancelRequested = fromDraft.cancelRequested;
    existing.runOutcomes = fromDraft.runOutcomes;
    existing.turnOutcomes = fromDraft.turnOutcomes;
    existing.error = fromDraft.error;
    existing.createdAt = Math.min(existing.createdAt, fromDraft.createdAt);
    existing.lastActivityAt = Math.max(
      existing.lastActivityAt,
      fromDraft.lastActivityAt
    );
  } else {
    immerDraft.threads[threadId] = fromDraft;
  }

  delete immerDraft.drafts[oldKey];

  immerDraft.promotionAliases[oldKey] = threadId;

  // Migrate the following so optimistic UI state survives the id rotation
  if (immerDraft.pendingRenames[oldKey]) {
    immerDraft.pendingRenames[threadId] = immerDraft.pendingRenames[oldKey];
    delete immerDraft.pendingRenames[oldKey];
  }
  if (immerDraft.pendingDeletes.has(oldKey)) {
    immerDraft.pendingDeletes.delete(oldKey);
    immerDraft.pendingDeletes.add(threadId);
  }
}

function deriveProjectFromContent(content: ContentPart[]): {
  entityName: string;
  projectName: string;
} | null {
  for (const part of content) {
    let entityName: string | undefined;
    let projectName: string | undefined;
    if (
      part.type === 'wandb-project' ||
      part.type === 'wandb-run' ||
      part.type === 'wandb-call' ||
      part.type === 'wandb-conversation' ||
      part.type === 'wandb-workspace-panel' ||
      part.type === 'wandb-web-page-state'
    ) {
      entityName = part.entityName;
      projectName = part.projectName;
    }
    if (entityName && projectName) {
      return {entityName, projectName};
    }
  }
  return null;
}
